/**
 * BaseFiltersEditor — edytor płaskiej listy stałych warunków filtrowania (AND).
 *
 * Używany w karcie „Filtry stałe" wbudowanego edytora pozycji menu (MenuItemInlineEditor).
 * Edytuje `FilterConfig[]` — każdy wiersz to: picker pola, dropdown operatora, input wartości.
 * Lista jest zawsze AND — brak grupowania OR w MVP.
 *
 * Obsługa operatorów:
 *   - 'is_null' / 'is_not_null' → brak inputu wartości
 *   - 'in' → input rozdzielony przecinkami (MVP — prosty string split przy użyciu)
 *   - pozostałe → jeden input tekstowy
 */

import { useTranslation } from 'react-i18next';
import {
  DOCUMENT_TYPE_FILTER_FIELD,
  type DocumentClass,
  type FilterConfig,
  type FilterOperator,
} from '@saas/shared';
import { Minus, Plus } from 'lucide-react';

import { Button } from '@/components/Button';
import { DocumentTypeMultiSelect } from '@/components/documents/DocumentTypeMultiSelect';
import { Dropdown } from '@/components/Dropdown';
import { isUserFilterField } from '@/lib/list-filter-extra-fields';
import { CURRENT_USER_FILTER_TOKEN } from '@/lib/list-filter-variables';

export interface FieldOption {
  id: string;
  name: string;
  /** Typ pola z katalogu — razem ze slugiem rozstrzyga, czy pokazać przycisk `@userId`. */
  fieldType?: string | null;
}

interface BaseFiltersEditorProps {
  value: FilterConfig[];
  onChange: (next: FilterConfig[]) => void;
  fields: FieldOption[];
  /**
   * Klasa dokumentu — gdy podana, wiersz pola `document_type_id` (Etap 3, blok D)
   * renderuje multi-select typów zamiast inputu tekstowego i blokuje operator na `in`.
   * `undefined` (domyślnie) = zachowanie sprzed bloku D, bez zmian.
   */
  documentClass?: DocumentClass;
  /** Źródło danych pickera typów dokumentów — patrz `DocumentTypeMultiSelect`. Domyślnie 'ite'. */
  scope?: 'ite' | 'tenant';
  /**
   * Czy przy polach użytkownika pokazywać przycisk `@userId`. Domyślnie nie — wołający włącza go
   * tylko tam, gdzie lista faktycznie stosuje filtry stałe (patrz MenuItemInlineEditor).
   */
  showCurrentUserButton?: boolean;
}

/** CSV (konwencja wartości operatora `in`) → tablica id, dla `DocumentTypeMultiSelect`. */
function csvToArray(value: unknown): string[] {
  // Dedup (Set) — TA SAMA semantyka co współdzielony `extractDocumentTypeIdsFromBaseFilters`
  // (@saas/shared), żeby konwencja wartości `in` była identyczna po obu stronach granicy.
  if (typeof value === 'string') {
    return [
      ...new Set(
        value
          .split(',')
          .map((v) => v.trim())
          .filter((v) => v.length > 0),
      ),
    ];
  }
  if (Array.isArray(value)) {
    return [...new Set(value.filter((v): v is string => typeof v === 'string'))];
  }
  return [];
}

/** Tablica id → CSV (konwencja wartości operatora `in`, zapisywana w `FilterConfig.value`). */
function arrayToCsv(ids: string[]): string {
  return ids.join(',');
}

/** Operatory bez wartości (pole wartości ukryte). */
const NO_VALUE_OPERATORS: FilterOperator[] = ['is_null', 'is_not_null'];

/** Etykiety operatorów — klucze i18n w `admin.menuItemBaseFilters.operators.*`. */
const OPERATOR_I18N_KEYS: Record<FilterOperator, string> = {
  eq: 'admin.menuItemBaseFilters.operators.eq',
  neq: 'admin.menuItemBaseFilters.operators.neq',
  contains: 'admin.menuItemBaseFilters.operators.contains',
  not_contains: 'admin.menuItemBaseFilters.operators.notContains',
  starts_with: 'admin.menuItemBaseFilters.operators.startsWith',
  ends_with: 'admin.menuItemBaseFilters.operators.endsWith',
  gt: 'admin.menuItemBaseFilters.operators.gt',
  gte: 'admin.menuItemBaseFilters.operators.gte',
  lt: 'admin.menuItemBaseFilters.operators.lt',
  lte: 'admin.menuItemBaseFilters.operators.lte',
  in: 'admin.menuItemBaseFilters.operators.in',
  is_null: 'admin.menuItemBaseFilters.operators.isNull',
  is_not_null: 'admin.menuItemBaseFilters.operators.isNotNull',
};

const ALL_OPERATORS: FilterOperator[] = [
  'eq',
  'neq',
  'contains',
  'not_contains',
  'starts_with',
  'ends_with',
  'gt',
  'gte',
  'lt',
  'lte',
  'in',
  'is_null',
  'is_not_null',
];

export function BaseFiltersEditor({
  value,
  onChange,
  fields,
  documentClass,
  scope,
  showCurrentUserButton = false,
}: BaseFiltersEditorProps) {
  const { t } = useTranslation();

  function addRow() {
    const firstField = fields[0]?.id ?? '';
    // Pole typu dokumentu bywa PIERWSZE w liście (prepend przez MenuItemInlineEditor
    // dla specjalne/internal) — nowy wiersz musi od razu dostać operator `in`, inaczej
    // powstałby niespójny stan (pole zablokowane na `in` w UI, ale zapisany `eq`).
    const operator: FilterOperator =
      firstField === DOCUMENT_TYPE_FILTER_FIELD && documentClass ? 'in' : 'eq';
    onChange([...value, { field: firstField, operator, value: '' }]);
  }

  function removeRow(index: number) {
    onChange(value.filter((_, i) => i !== index));
  }

  function updateRow(index: number, next: FilterConfig) {
    onChange(value.map((row, i) => (i === index ? next : row)));
  }

  if (fields.length === 0) {
    return (
      <p className="py-2 text-xs text-text-muted">
        {t('admin.menuItemBaseFilters.noFields', {
          defaultValue: 'Brak pól do filtrowania dla tej kartoteki.',
        })}
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {value.length === 0 ? (
        <p className="py-1 text-xs text-text-muted">
          {t('admin.menuItemBaseFilters.emptyConditions', {
            defaultValue: 'Brak warunków — lista pokazuje wszystkie rekordy.',
          })}
        </p>
      ) : (
        <div className="space-y-2">
          {value.map((row, index) => (
            <FilterRow
              key={index}
              row={row}
              fields={fields}
              documentClass={documentClass}
              scope={scope}
              showCurrentUserButton={showCurrentUserButton}
              onChange={(next) => updateRow(index, next)}
              onRemove={() => removeRow(index)}
              t={t}
            />
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={addRow}
        className="flex items-center gap-1.5 rounded-md border border-accent-300 px-3 py-1.5 text-xs font-medium text-accent-600 hover:bg-accent-50 dark:hover:bg-accent-950/20"
        data-testid="base-filters-add-row"
      >
        <Plus className="h-3.5 w-3.5" />
        {t('admin.menuItemBaseFilters.addCondition', { defaultValue: 'Dodaj warunek' })}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FilterRow
// ---------------------------------------------------------------------------

function FilterRow({
  row,
  fields,
  documentClass,
  scope,
  showCurrentUserButton,
  onChange,
  onRemove,
  t,
}: {
  row: FilterConfig;
  fields: FieldOption[];
  documentClass?: DocumentClass;
  scope?: 'ite' | 'tenant';
  showCurrentUserButton: boolean;
  onChange: (next: FilterConfig) => void;
  onRemove: () => void;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  // Pole typu dokumentu (Etap 3, blok D) — TYLKO gdy caller podał documentClass
  // (czyli klasa dopuszcza zawężenie, patrz MenuItemInlineEditor). Bez tego (documentClass
  // undefined) pole zachowuje się jak każde inne — nawet gdyby jego `field` slug pasował.
  const isDocumentTypeField = !!documentClass && row.field === DOCUMENT_TYPE_FILTER_FIELD;
  const hideValue = NO_VALUE_OPERATORS.includes(row.operator);
  const rowField = fields.find((f) => f.id === row.field);
  const showCurrentUser =
    showCurrentUserButton &&
    !isDocumentTypeField &&
    !hideValue &&
    row.operator !== 'in' &&
    !!rowField &&
    isUserFilterField(rowField);

  function handleFieldChange(nextField: string) {
    if (nextField === DOCUMENT_TYPE_FILTER_FIELD && documentClass) {
      // Zawężenie do typów dokumentu ma sens WYŁĄCZNIE z operatorem `in` (multi-select) —
      // wymuszamy go od razu przy wyborze pola, zamiast zostawiać niespójny stan.
      onChange({ ...row, field: nextField, operator: 'in' });
    } else if (row.field === DOCUMENT_TYPE_FILTER_FIELD) {
      // Odejście OD pola typu dokumentu na zwykłe pole: zresetuj operator `in` i wartość
      // (CSV z UUID-ami typów) do stanu neutralnego — inaczej powstałby bezsensowny wiersz
      // typu `{ field:'name', operator:'in', value:'uuid,uuid' }`.
      onChange({ ...row, field: nextField, operator: 'eq', value: '' });
    } else {
      onChange({ ...row, field: nextField });
    }
  }

  function handleOperatorChange(op: FilterOperator) {
    const next: FilterConfig = { ...row, operator: op };
    // Czyść wartość gdy operator nie używa wartości
    if (NO_VALUE_OPERATORS.includes(op)) {
      next.value = null;
    }
    onChange(next);
  }

  return (
    <div className="flex flex-wrap items-start gap-2" data-testid="base-filter-row">
      {/* Picker pola */}
      <Dropdown
        value={row.field}
        onChange={handleFieldChange}
        className="min-w-[7rem] flex-1 rounded-md border border-border-secondary bg-bg-primary px-2 py-1.5 text-xs text-text-primary focus:ring-1 focus:ring-accent-500"
        data-testid="base-filter-field"
        options={fields.map((f) => ({ value: f.id, label: f.name }))}
      />

      {/* Operator — zablokowany na `in` dla pola typu dokumentu (semantyka multi-select,
          nie da się go zmienić — patrz isDocumentTypeField powyżej). */}
      <Dropdown
        value={isDocumentTypeField ? 'in' : row.operator}
        onChange={(v) => handleOperatorChange(v as FilterOperator)}
        disabled={isDocumentTypeField}
        className="min-w-[7rem] flex-1 rounded-md border border-border-secondary bg-bg-primary px-2 py-1.5 text-xs text-text-primary focus:ring-1 focus:ring-accent-500"
        data-testid="base-filter-operator"
        options={ALL_OPERATORS.map((op) => ({
          value: op,
          label: t(OPERATOR_I18N_KEYS[op], { defaultValue: op }),
        }))}
      />

      {/* Wartość — multi-select nazw typów dla document_type_id, ukryta dla
          is_null / is_not_null, w pozostałych przypadkach input tekstowy. */}
      {isDocumentTypeField ? (
        <div className="min-w-[6rem] flex-1" data-testid="base-filter-value-document-type">
          <DocumentTypeMultiSelect
            documentClass={documentClass}
            scope={scope ?? 'ite'}
            value={csvToArray(row.value)}
            // Operator `in` wymuszony jawnie także tutaj (obok blokady dropdowna) — stan nie
            // może się rozjechać z UI, gdyby wiersz trafił z zewnątrz z innym operatorem.
            onChange={(ids) => onChange({ ...row, operator: 'in', value: arrayToCsv(ids) })}
          />
        </div>
      ) : !hideValue ? (
        <input
          type="text"
          value={
            typeof row.value === 'string' ? row.value : row.value != null ? String(row.value) : ''
          }
          onChange={(e) => onChange({ ...row, value: e.target.value })}
          placeholder={
            row.operator === 'in'
              ? t('admin.menuItemBaseFilters.valuePlaceholderIn', {
                  defaultValue: 'wartości po przecinku',
                })
              : t('admin.menuItemBaseFilters.valuePlaceholder', { defaultValue: 'wartość' })
          }
          className="min-w-[6rem] flex-1 rounded-md border border-border-secondary bg-bg-primary px-2 py-1.5 text-xs text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-accent-500"
          data-testid="base-filter-value"
        />
      ) : (
        /* Placeholder przestrzenny gdy brak inputu — zachowuje wyrównanie przycisk Usuń */
        <div className="min-w-[6rem] flex-1" />
      )}

      {/* `@userId` — zalogowany użytkownik (osoba oglądająca listę, nie admin ustawiający filtr).
          Tylko przy polach z identyfikatorem użytkownika; ten sam przycisk co w szybkich filtrach. */}
      {showCurrentUser && (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => onChange({ ...row, value: CURRENT_USER_FILTER_TOKEN })}
          title={t('tenant.listBuilder.qf.currentUserHint')}
          className="flex-shrink-0"
          data-testid="base-filter-insert-current-user"
        >
          {t('tenant.listBuilder.qf.currentUserButton')}
        </Button>
      )}

      {/* Usuń wiersz */}
      <button
        type="button"
        onClick={onRemove}
        className="flex-shrink-0 rounded-md border border-border-secondary p-1.5 text-text-muted hover:border-red-300 hover:text-red-600"
        title={t('common.delete', { defaultValue: 'Usuń' })}
        data-testid="base-filter-remove"
      >
        <Minus className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
